import { Analytics } from '@analytics';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import TextIconInformationBox from '@components/Onboarding/TextIconInformationBox';
import { TextIconBoxType } from '@components/Onboarding/constants';
import { orderCardLink, StyleBreakpoints } from '@constants';
import { Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FilterNone as CopyIcon, GetApp as DownloadIcon } from '@material-ui/icons';
import { Alert as MuiAlert } from '@material-ui/lab';
import { formatRegionalLinks, isPaymentsCompany } from '@utils/helpers';
import { generateA4Printout } from '@utils/printing_templates/generateA4Printout';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import messages from './messages';
import RFIDIconColor from '@assets/images/RFID.svg';
import PaymentLinkQRGeneratorModal from '@components/TapToTip/PaymentLinksQRGeneratorModal';

const useStyles = makeStyles(theme => ({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

// Shared Tailwind class strings (visual only)
const buttonBaseClasses =
  'items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors cursor-pointer border-0 no-underline text-sm';
const primaryButtonClasses = `inline-flex ${buttonBaseClasses}`;
const shareButtonClasses = `flex md:hidden ${buttonBaseClasses}`;
const cardClasses =
  'flex flex-col w-full max-w-[390px] gap-5 bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6';
// Cards inside the masonry column layout: keep columns from splitting a card
// and use bottom margin for vertical rhythm (column gap handles horizontal).
const masonryCardClasses = `${cardClasses} break-inside-avoid mb-6`;
const copyLinkRowClasses =
  'grid grid-cols-[1fr_auto] items-center gap-4 w-full rounded-xl border border-solid border-slate-200 bg-white px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors relative';
const copyLinkTextClasses =
  'font-semibold text-slate-800 text-xs text-center overflow-hidden text-ellipsis whitespace-nowrap min-w-0';
const copyIconClasses = 'cursor-pointer text-slate-400 hover:text-slate-900 transition-colors';
const qrPanelClasses =
  'flex flex-col justify-center items-center p-3 bg-white rounded-2xl border border-solid border-slate-200';
const qrCodeClasses =
  'flex justify-center items-center p-2.5 border-[3px] border-solid border-slate-900 rounded-md bg-white';

function TipDirectLinkInformation({ user }) {
  const intl = useIntl();
  const classes = useStyles();
  const canvasRef = useRef();
  const personalCanvasRef = useRef();
  const guideGalleryQRRef = useRef();

  const [showAlert, setShouldShowCopyAlert] = useState(false);
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false);

  const linkUrl = formatRegionalLinks(`t/g/${user.userId}`);
  const guideGallerylinkUrl = formatRegionalLinks(`t/all/g/${user.companyId}`);
  const companyInviteLinkUrl = formatRegionalLinks(`signupfrominvite/${user.inviteCode}`);

  const previewTippingLink = `${linkUrl}?preview=true`;

  const handlePrintPage = () => {
    Analytics.track('download a4 printout button clicked', {
      page: 'tip direct'
    });
    generateA4Printout(user, process.env.REGION);
  };

  function downloadQRCodeAsPNG(currentRef, filename) {
    const canvas = currentRef?.current.children[0];
    if (!canvas) {
      throw new Error('Canvas not found');
    }

    const imageData = canvas.toDataURL('image/png');
    downloadStringAsFile(imageData, filename);
  }

  function downloadStringAsFile(data, filename) {
    let a = document.createElement('a');
    a.download = filename;
    a.href = data;
    a.click();
  }

  const imageSettings = {
    height: window.innerWidth <= StyleBreakpoints.sm ? 240 : 300,
    width: window.innerWidth <= StyleBreakpoints.sm ? 240 : 300,
    x: 0,
    y: 0
  };

  const size = window.innerWidth <= StyleBreakpoints.sm ? 240 : 300;


  const userIsPaymentsCompany = isPaymentsCompany(user.companyId, user);

  if (userIsPaymentsCompany) {
    return (
      <>
        {/* New Payments Page */}
        <TopNav />
        <div className={classes.outerContainer}>
          <TipDirectLeftMenu selectedTab={'TipDirect'} />
          <div className="flex-1 flex flex-col p-6 md:p-8 w-full max-w-7xl mx-auto min-w-0">
            <div className="mb-8 shrink-0">
              <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
                {intl.formatMessage(messages.pageHeadingPayments)}
              </h1>
              <p className="text-slate-500 mt-2">
                {intl.formatMessage(messages.pageDescPayments)}
              </p>
            </div>
            <div className="flex flex-wrap content-start items-start justify-center md:justify-start gap-6 min-w-0">
              <div className={cardClasses}>
                <div className="font-bold text-slate-800">
                  {intl.formatMessage(messages.takePayment)}
                </div>
                <div className="text-sm text-slate-500">
                  {intl.formatMessage(messages.takePaymentDesc)}
                </div>
                <button
                  type="button"
                  className={primaryButtonClasses}
                  onClick={() => setIsPaymentLinkModalOpen(true)}>
                  <img src={RFIDIconColor} alt="" className="w-6 h-6 mr-2" />
                  {intl.formatMessage(messages.takePayment)}
                </button>
              </div>
            </div>
          </div>
        </div>

        <PaymentLinkQRGeneratorModal
          isOpen={isPaymentLinkModalOpen}
          setIsOpen={setIsPaymentLinkModalOpen}
          currency={user.profile.currency}
          userId={user.userId}
          companyName={user.companyName}
        />
        <Snackbar
          open={showAlert}
          autoHideDuration={10000}
          onClose={() => {
            setShouldShowCopyAlert(false);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <MuiAlert
            variant="filled"
            elevation={6}
            severity="success"
            onClose={() => {
              setShouldShowCopyAlert(false);
            }}>
            {intl.formatMessage(messages.copiedToClipboard)}
          </MuiAlert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <TopNav />
      <div className={classes.outerContainer}>
        <TipDirectLeftMenu selectedTab={'TipDirect'} />
        <div className="flex-1 flex flex-col p-6 md:p-8 w-full max-w-7xl mx-auto min-w-0">
          <div className="mb-8 shrink-0">
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
              {intl.formatMessage(
                (user.userFeatures?.tipsDisabled ?? user.tipsDisabled)
                  ? messages.pageHeadingReviews
                  : messages.pageHeadingTips
              )}
            </h1>
            <p className="text-slate-500 mt-2">
              {intl.formatMessage(
                (user.userFeatures?.tipsDisabled ?? user.tipsDisabled)
                  ? messages.pageDescReviews
                  : messages.pageDescTips
              )}
            </p>
          </div>
          <div className="flex flex-wrap content-start gap-6 min-w-0">
        <div className="w-full sm:w-auto flex flex-col items-center gap-6 shrink-0">
          <div className={cardClasses}>
            <div className="h-10 flex items-center justify-center font-bold text-slate-800 bg-white rounded-xl border border-solid border-slate-200 shadow-sm">{`${user.profile.firstName} ${user.profile.lastName
              }`}</div>
            <TextIconInformationBox
              card={(user.userFeatures?.tipsDisabled ?? user.tipsDisabled) ? TextIconBoxType.PersonalQRReviews : TextIconBoxType.PersonalQR}
              grid={true}
              noBackgroundCircle={true}
            />

            <div className={qrPanelClasses}>
              <div className={qrCodeClasses} ref={personalCanvasRef}>
                <QRCodeCanvas value={linkUrl} size={size} imageSettings={imageSettings} />
              </div>
            </div>
            <button
              type="button"
              className={primaryButtonClasses}
              onClick={() => {
                Analytics.track('download personal QR code clicked', {
                  page: 'tip direct'
                });
                downloadQRCodeAsPNG(personalCanvasRef, 'TripAdmit-Tap-To-Tip-QR-Code.png');
              }}>
              {intl.formatMessage(messages.downloadImage)}
              <DownloadIcon className="ml-2" />
            </button>
          </div>
        </div>

        <div className="flex-1 basis-[390px] min-w-0">
          <div className="w-full max-w-[804px] columns-[390px] gap-6">
          <div className={masonryCardClasses}>
            <TextIconInformationBox
              card={(user.userFeatures?.tipsDisabled ?? user.tipsDisabled) ? TextIconBoxType.PersonalTippingReviews : TextIconBoxType.PersonalTipping}
              noBackgroundCircle={true}
            />
            <div
              role="button"
              color="primary"
              className={copyLinkRowClasses}
              tabIndex={0}
              onKeyDown={() => {
                Analytics.track('copy tipping link', {
                  page: 'tip direct'
                });
                navigator.clipboard.writeText(linkUrl);
                setShouldShowCopyAlert(true);
              }}
              onClick={() => {
                Analytics.track('copy tipping link', {
                  page: 'tip direct'
                });
                navigator.clipboard.writeText(linkUrl);
                setShouldShowCopyAlert(true);
              }}>
              <div className={copyLinkTextClasses}>{linkUrl}</div>
              <CopyIcon className={copyIconClasses} />
            </div>
          </div>

          <div className={masonryCardClasses}>
            <TextIconInformationBox card={TextIconBoxType.PrintOut} noBackgroundCircle={true} />
            <button
              type="button"
              className={primaryButtonClasses}
              onClick={() => handlePrintPage()}>
              {intl.formatMessage(messages.downloadPrintout)}
            </button>
          </div>

          <div className={masonryCardClasses}>
            <TextIconInformationBox
              card={(user.userFeatures?.tipsDisabled ?? user.tipsDisabled) ? TextIconBoxType.PreviewTippingFlowReviews : TextIconBoxType.PreviewTippingFlow}
              noBackgroundCircle={true}
            />
            <a
              onClick={() => {
                Analytics.track('Preview tipping flow clicked');
              }}
              className={primaryButtonClasses}
              target="_blank"
              href={previewTippingLink}>
              {intl.formatMessage(messages.previewTippingTitle)}
            </a>
          </div>

          {user.role === 'system_admin' && (
            <>
              <div className={masonryCardClasses}>
                <TextIconInformationBox
                  card={(user.userFeatures?.tipsDisabled ?? user.tipsDisabled) ? TextIconBoxType.GuideGalleryReviews : TextIconBoxType.GuideGallery}
                  noBackgroundCircle={true}
                />
                <div className="flex flex-col gap-3 w-full">
                  <div style={{ display: 'none' }} ref={guideGalleryQRRef}>
                    <QRCodeCanvas
                      value={guideGallerylinkUrl}
                      size={size}
                      imageSettings={imageSettings}
                    />
                  </div>
                  <div
                    role="button"
                    color="primary"
                    className={copyLinkRowClasses}
                    tabIndex={0}
                    onKeyDown={() => {
                      Analytics.track('copy guide gallery link', {
                        page: 'tip direct'
                      });
                      navigator.clipboard.writeText(guideGallerylinkUrl);
                      setShouldShowCopyAlert(true);
                    }}
                    onClick={() => {
                      Analytics.track('copy guide gallery link', {
                        page: 'tip direct'
                      });
                      navigator.clipboard.writeText(guideGallerylinkUrl);
                      setShouldShowCopyAlert(true);
                    }}>
                    <div className={copyLinkTextClasses}>{guideGallerylinkUrl}</div>
                    <CopyIcon className={copyIconClasses} />
                  </div>
                  <button
                    type="button"
                    className={primaryButtonClasses}
                    onClick={() => {
                      Analytics.track('view Guide Gallery clicked', {
                        page: 'tip direct'
                      });
                      window.location.href = guideGallerylinkUrl;
                    }}>
                    {intl.formatMessage(messages.viewGuideGallery)}
                  </button>
                  <button
                    type="button"
                    className={primaryButtonClasses}
                    onClick={() => {
                      Analytics.track('download guide gallery qr code clicked', {
                        page: 'tip direct'
                      });

                      downloadQRCodeAsPNG(
                        guideGalleryQRRef,
                        'TripAdmit-Tap-To-Tip-Guide-Gallery-QR-Code.png'
                      );
                    }}>
                    {intl.formatMessage(messages.downloadGalleryQR)}
                    <DownloadIcon className="ml-2" />
                  </button>
                </div>
              </div>
            </>
          )}

          <div className={masonryCardClasses}>
            <TextIconInformationBox card={TextIconBoxType.OrderCards} noBackgroundCircle={true} />
            <a
              className={primaryButtonClasses}
              target="_blank"
              href={orderCardLink}>
              {intl.formatMessage(messages.orderCards)}
            </a>
          </div>

          {user.role === 'system_admin' && user.inviteCode && (
            <div className={masonryCardClasses}>
              <div className={qrPanelClasses}>
                <div className={qrCodeClasses} ref={canvasRef}>
                  <QRCodeCanvas
                    value={companyInviteLinkUrl}
                    size={size}
                    imageSettings={imageSettings}
                  />
                </div>
              </div>
              <TextIconInformationBox
                card={TextIconBoxType.CompanyInviteLink}
                noBackgroundCircle={true}
              />
              <div className="flex flex-col gap-3 w-full">
                <button
                  type="button"
                  className={primaryButtonClasses}
                  onClick={() => {
                    Analytics.track('copy company invite link clicked', {
                      page: 'tip direct'
                    });
                    navigator.clipboard.writeText(companyInviteLinkUrl);
                    setShouldShowCopyAlert(true);
                  }}>
                  {intl.formatMessage(messages.copyLink)}
                  <CopyIcon className="ml-2" />
                </button>
                <button
                  type="button"
                  className={shareButtonClasses}
                  onClick={() => {
                    Analytics.track('share company invite link clicked', {
                      page: 'tip direct'
                    });

                    navigator.share({
                      title: 'TripAdmit',
                      url: companyInviteLinkUrl
                    });
                  }}>
                  {intl.formatMessage(messages.share)}
                  <DownloadIcon className="ml-2" />
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
        </div>
        </div>
      </div>
      <Snackbar
        open={showAlert}
        autoHideDuration={10000}
        onClose={() => {
          setShouldShowCopyAlert(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="success"
          onClose={() => {
            setShouldShowCopyAlert(false);
          }}>
          {intl.formatMessage(messages.copiedToClipboard)}
        </MuiAlert>
      </Snackbar>

    </>
  );
}

const mapStateToProps = state => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(TipDirectLinkInformation);
